import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Video, Wand2, Loader2, Copy, Trash2, CheckCircle2, Clapperboard, Instagram, RefreshCw, Download, Film, Play, Volume2 } from "lucide-react";

const STATUS = {
  draft: "border-hairline text-bone/70",
  approved: "border-amber-500/50 text-amber-400",
  posted: "border-emerald-500/50 text-emerald-400",
};

export default function ReelsPage() {
  const [reels, setReels] = useState([]);
  const [articles, setArticles] = useState([]);
  const [articleId, setArticleId] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previews, setPreviews] = useState({});
  const [vidOpts, setVidOpts] = useState({});
  const pollRef = useRef({});

  const getOpt = (id) => ({ voice: "onyx", music: true, ...(vidOpts[id] || {}) });
  const setOpt = (id, patch) => setVidOpts((o) => ({ ...o, [id]: { ...getOpt(id), ...patch } }));

  const pollVideo = useCallback((id) => {
    if (pollRef.current[id]) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/reels/${id}`);
        setReels((l) => l.map((x) => (x.id === id ? data : x)));
        if (data.video_status === "generating") {
          pollRef.current[id] = setTimeout(tick, 4000);
        } else {
          delete pollRef.current[id];
          if (data.video_status === "ready") toast.success("Reel video ready — download it below");
          if (data.video_status === "failed") toast.error(data.video_error || "Video render failed");
        }
      } catch { pollRef.current[id] = setTimeout(tick, 5000); }
    };
    pollRef.current[id] = setTimeout(tick, 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get("/reels"), api.get("/reels/articles")]);
      const list = r.data || [];
      setReels(list);
      setArticles(a.data || []);
      list.filter((x) => x.video_status === "generating").forEach((x) => pollVideo(x.id));
    } catch { toast.error("Failed to load reels"); }
    finally { setLoading(false); }
  }, [pollVideo]);

  useEffect(() => { load(); }, [load]);

  const makeVideo = async (r) => {
    const opt = getOpt(r.id);
    try {
      await api.post(`/reels/${r.id}/video`, { voiceover: true, voice: opt.voice, music: opt.music });
      setReels((l) => l.map((x) => (x.id === r.id ? { ...x, video_status: "generating", video_error: null } : x)));
      toast.message("Producing your Reel video — voiceover + slides (~30–60s)");
      pollVideo(r.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start video"); }
  };

  const downloadVideo = async (r) => {
    try {
      const res = await api.get(`/reels/${r.id}/video/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(r.title || "reel").replace(/[^a-z0-9 \-_]/gi, "").trim() || "reel"}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Download failed"); }
  };

  const togglePreview = async (r) => {
    if (previews[r.id]) {
      URL.revokeObjectURL(previews[r.id]);
      setPreviews((p) => { const n = { ...p }; delete n[r.id]; return n; });
      return;
    }
    try {
      const res = await api.get(`/reels/${r.id}/video/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviews((p) => ({ ...p, [r.id]: url }));
    } catch { toast.error("Preview failed"); }
  };


  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reels/generate", { article_id: articleId || undefined });
      setReels((list) => [data, ...list]);
      toast.success("Reel package ready — review below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const setStatus = async (r, status) => {
    try { const { data } = await api.patch(`/reels/${r.id}`, { status }); setReels((l) => l.map((x) => x.id === r.id ? data : x)); toast.success(`Marked ${status}`); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (r) => {
    try { await api.delete(`/reels/${r.id}`); setReels((l) => l.filter((x) => x.id !== r.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  const copyAll = async (r) => {
    const scenes = (r.scenes || []).map((s, i) => `Scene ${i + 1}\n  On-screen: ${s.caption}\n  Visual: ${s.visual}\n  VO: ${s.voiceover}`).join("\n\n");
    const text = `HOOK: ${r.hook}\n\n${scenes}\n\nCAPTION:\n${r.caption}\n\n${(r.hashtags || []).join(" ")}`;
    try { await navigator.clipboard.writeText(text); toast.success("Reel script copied"); } catch {}
  };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-mono text-[11px] text-bone/50 flex items-center gap-2"><Video className="w-3.5 h-3.5" /> INSTAGRAM REEL STUDIO</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tightest mt-2">Daily Reel Studio</h1>
            <p className="text-bone/60 mt-2 max-w-xl text-sm">
              Technical Reel packages for <span className="text-safety">@wisewit.solutions</span>, built from your Lingent articles — script, storyboard, captions, hashtags &amp; a cover. A fresh one is auto-generated daily; review, then post.
            </p>
          </div>
          <div className="flex items-stretch gap-2">
            <select value={articleId} onChange={(e) => setArticleId(e.target.value)} data-testid="reel-article-select"
              className="label-mono text-xs bg-ink border border-hairline text-bone/80 px-3 h-11 focus:outline-none focus:ring-1 focus:ring-klein max-w-[260px]">
              <option value="">Latest article (default)</option>
              {articles.map((a) => <option key={a.id} value={a.id}>{a.title?.slice(0, 48)}</option>)}
            </select>
            <Button onClick={generate} disabled={generating} data-testid="reel-generate-btn"
              className="rounded-none bg-safety text-white hover:bg-safety/90 label-mono">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {generating ? "Producing…" : "Generate Reel"}
            </Button>
            <Button onClick={load} variant="outline" className="rounded-none border-hairline bg-transparent hover:bg-surface" data-testid="reel-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-bone/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : reels.length === 0 ? (
          <div className="border border-dashed border-hairline p-16 text-center text-bone/50">
            <Clapperboard className="w-8 h-8 mx-auto mb-3 text-bone/30" />
            No reels yet. Hit <span className="text-safety">Generate Reel</span> to produce your first technical Reel package.
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5" data-testid="reels-grid">
            {reels.map((r) => (
              <div key={r.id} data-testid={`reel-${r.id}`} className="border border-hairline bg-surface/40">
                <div className="flex gap-4 p-4">
                  {r.cover_image ? (
                    <img src={r.cover_image} alt="cover" className="w-24 h-40 object-cover border border-hairline flex-shrink-0" data-testid={`reel-cover-${r.id}`} />
                  ) : (
                    <div className="w-24 h-40 bg-ink border border-hairline flex items-center justify-center text-bone/30 flex-shrink-0"><Video className="w-6 h-6" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`label-mono text-[10px] px-2 py-0.5 border ${STATUS[r.status] || STATUS.draft}`}>{r.status}</span>
                      {r.auto && <span className="label-mono text-[10px] px-2 py-0.5 border border-klein/40 text-klein">AUTO</span>}
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight">{r.title}</h3>
                    <p className="text-safety text-sm mt-1">“{r.hook}”</p>
                    <p className="text-bone/40 label-mono text-[10px] mt-1 truncate">from: {r.source_article_title}</p>
                  </div>
                </div>

                <div className="px-4 pb-3 space-y-1.5">
                  {(r.scenes || []).map((s, i) => (
                    <div key={i} className="border-l-2 border-hairline pl-3">
                      <div className="text-sm text-bone/90"><span className="text-bone/40 label-mono text-[10px] mr-1">{i + 1}.</span>{s.caption}</div>
                      <div className="text-bone/50 text-xs">🎬 {s.visual}</div>
                      <div className="text-bone/60 text-xs italic">🎙 {s.voiceover}</div>
                    </div>
                  ))}
                </div>

                <div className="px-4 pb-3">
                  <div className="label-mono text-[10px] text-bone/40 mb-1">CAPTION</div>
                  <p className="text-bone/80 text-sm whitespace-pre-line line-clamp-4">{r.caption}</p>
                  <div className="text-klein text-xs mt-1.5 line-clamp-2">{(r.hashtags || []).join(" ")}</div>
                </div>

                <div className="flex flex-wrap gap-1.5 p-4 border-t border-hairline">
                  <Button size="sm" onClick={() => copyAll(r)} className="rounded-none bg-bone text-ink hover:bg-bone/90 label-mono text-xs h-8" data-testid={`reel-copy-${r.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy script
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open("https://www.instagram.com/wisewit.solutions/", "_blank", "noopener")}
                    className="rounded-none border-hairline bg-transparent hover:bg-surface label-mono text-xs h-8" data-testid={`reel-ig-${r.id}`}>
                    <Instagram className="w-3.5 h-3.5 mr-1" /> Open IG
                  </Button>
                  {!["approved", "posted"].includes(r.status) && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r, "approved")} className="rounded-none border-amber-500/40 text-amber-400 hover:bg-amber-500/10 label-mono text-xs h-8" data-testid={`reel-approve-${r.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {r.status !== "posted" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r, "posted")} className="rounded-none border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 label-mono text-xs h-8" data-testid={`reel-posted-${r.id}`}>
                      Posted
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(r)} className="rounded-none border-hairline text-bone/50 hover:bg-surface ml-auto h-8" data-testid={`reel-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>

                <div className="p-4 border-t border-hairline bg-ink/40" data-testid={`reel-video-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="label-mono text-[10px] text-bone/40 flex items-center gap-1.5"><Film className="w-3.5 h-3.5" /> POSTABLE VIDEO (9:16 MP4)</div>
                    {r.video_status === "ready" && r.video_voiced && (
                      <span className="label-mono text-[9px] text-klein flex items-center gap-1"><Volume2 className="w-3 h-3" /> AI VOICEOVER</span>
                    )}
                  </div>

                  {r.video_status === "generating" ? (
                    <div className="flex items-center gap-2 text-bone/60 text-sm py-1" data-testid={`reel-video-generating-${r.id}`}>
                      <Loader2 className="w-4 h-4 animate-spin text-safety" /> Rendering voiceover &amp; slides… hang tight (~30–60s)
                    </div>
                  ) : (
                    <>
                    <div className="flex flex-wrap items-center gap-3 mb-2.5">
                      <label className="flex items-center gap-1.5 text-[11px] text-bone/50 label-mono">
                        VOICE
                        <select value={getOpt(r.id).voice} onChange={(e) => setOpt(r.id, { voice: e.target.value })}
                          className="bg-ink border border-hairline text-bone/80 px-2 h-7 text-xs focus:outline-none focus:ring-1 focus:ring-klein" data-testid={`reel-voice-${r.id}`}>
                          <option value="onyx">Deep</option>
                          <option value="sage">Warm</option>
                          <option value="shimmer">Bright</option>
                          <option value="nova">Energetic</option>
                          <option value="echo">Calm</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-bone/60 label-mono cursor-pointer select-none">
                        <input type="checkbox" checked={getOpt(r.id).music} onChange={(e) => setOpt(r.id, { music: e.target.checked })}
                          className="accent-safety w-3.5 h-3.5" data-testid={`reel-music-${r.id}`} />
                        BRAND MUSIC
                      </label>
                    </div>
                    {r.video_status === "ready" ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          <Button size="sm" onClick={() => downloadVideo(r)} className="rounded-none bg-safety text-white hover:bg-safety/90 label-mono text-xs h-8" data-testid={`reel-download-${r.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Download MP4
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => togglePreview(r)} className="rounded-none border-hairline bg-transparent hover:bg-surface label-mono text-xs h-8" data-testid={`reel-preview-${r.id}`}>
                            <Play className="w-3.5 h-3.5 mr-1" /> {previews[r.id] ? "Hide preview" : "Preview"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => makeVideo(r)} className="rounded-none border-hairline bg-transparent hover:bg-surface label-mono text-xs h-8" data-testid={`reel-revideo-${r.id}`}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rebuild
                          </Button>
                        </div>
                        {previews[r.id] && (
                          <video src={previews[r.id]} controls playsInline className="w-40 border border-hairline bg-black" data-testid={`reel-video-el-${r.id}`} />
                        )}
                        <p className="text-bone/40 text-[11px]">Download it, then tap <span className="text-safety">Open IG</span> above and upload as a Reel. On Instagram you can keep this voiceover or swap in trending audio.</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Button size="sm" onClick={() => makeVideo(r)} className="rounded-none bg-safety text-white hover:bg-safety/90 label-mono text-xs h-8" data-testid={`reel-makevideo-${r.id}`}>
                          <Film className="w-3.5 h-3.5 mr-1" /> Create video
                        </Button>
                        {r.video_status === "failed" && (
                          <p className="text-red-400/80 text-[11px]" data-testid={`reel-video-error-${r.id}`}>Render failed: {r.video_error || "unknown error"}. Try again.</p>
                        )}
                      </div>
                    )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
